export type MessageAttachmentChip = {
  id: string;
  name: string;
  sizeLabel?: string;
};

export type ChatKbSource = {
  id: string;
  name: string;
  type?: string;
  project_id?: string | null;
  scope?: string | null;
};

const FILE_MARKER = /\[Attached file:\s*/i;
const KB_MARKER = /\[KB source:\s*/i;

function firstMarkerIndex(text: string): number {
  const fileIdx = text.search(FILE_MARKER);
  const kbIdx = text.search(KB_MARKER);
  const hits = [fileIdx, kbIdx].filter((i) => i >= 0);
  return hits.length ? Math.min(...hits) : -1;
}

/** Backend persists attachments by appending
 * `[Attached file: name.csv (text/csv) [uploaded, file_key: …]]` to the
 * user message. The client also appends `[KB source: name (id: …)]`
 * so the model sees the selected knowledge file. */
export function splitAttachedFileMarkers(text: string): {
  text: string;
  attachments: MessageAttachmentChip[];
  kbSources: MessageAttachmentChip[];
} {
  if (!text) return { text: "", attachments: [], kbSources: [] };
  const idx = firstMarkerIndex(text);
  if (idx === -1) return { text, attachments: [], kbSources: [] };

  const attachments: MessageAttachmentChip[] = [];
  const kbSources: MessageAttachmentChip[] = [];
  const rest = text.slice(idx);
  let match: RegExpExecArray | null;
  const fileRe = /\[Attached file:\s*([^(\]\n]+)/gi;
  while ((match = fileRe.exec(rest))) {
    const name = match[1].trim();
    if (name) attachments.push({ id: `att_${name}`, name, sizeLabel: "file" });
  }
  const kbRe = /\[KB source:\s*([^(\]\n]+)/gi;
  while ((match = kbRe.exec(rest))) {
    const name = match[1].trim();
    if (name) kbSources.push({ id: `kb_${name}`, name, sizeLabel: "KB" });
  }
  return { text: text.slice(0, idx).trim(), attachments, kbSources };
}

export function kbMessageSuffix(sources: { id: string; name: string }[]): string {
  if (!sources.length) return "";
  return sources.map((s) => `[KB source: ${s.name} (id: ${s.id})]`).join(" ");
}

export function displayConversationLabel(text: string): string {
  const parsed = splitAttachedFileMarkers(text);
  return parsed.text || parsed.attachments[0]?.name || parsed.kbSources[0]?.name || text;
}
